import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-client';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, FileDown, FileText, PenLine, Lock, Package, ExternalLink, Copy, QrCode, Search, X, Plus, Trash2, UserCheck, Mail, Link2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from '@/app/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle as UIDialogTitle,
} from '@/app/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  useJSA,
  useUpdateJSA,
  useDeleteJSA,
  useSubmitJSAForSignatures,
  useCloseJSA,
  useActivateJSA,
  useSignJSA,
  useReaffirmJSARevalidation,
  useAddDispatchedCrewToJSA,
  useRequireJSAResign,
  useAcknowledgeSignJSA,
  useExpectedSigners,
  useReDispositionSigner,
  useDuplicateJSA,
  useAddExpectedSigner,
  useRemoveExpectedSigner,
  useJSASigningQR,
  useGuestSigners,
  useAddGuestSigner,
  useRescuePlans,
  useLinkRescuePlanToJSA,
} from '@/hooks/use-safety';
import { usePermissions } from '@/hooks/use-permissions';
import { useAuth } from '@/contexts/auth-context';
import { useBranding } from '@/contexts/branding-context';
import { usePersons } from '@/hooks/use-persons';
import { QRCodeModal } from '@/components/safety/qr-code-modal';
import { KioskSignModal } from '@/components/safety/kiosk-sign-modal';
import { JSAHazardCarousel, type HazardAcknowledgement } from '@/components/safety/jsa-hazard-carousel';
import type { KioskSignResult } from '@/components/safety/kiosk-sign-modal';
import { AlertTriangle, CheckCircle2, Clock, UserPlus } from 'lucide-react';
import { SignaturePad } from '@/components/safety/signature-pad';
import { PdfOfflineNotice } from '@/app/components/offline/pdf-offline-notice';
import { OfflineSignatureIndicator } from '@/app/components/offline/offline-signature-indicator';
import { ErrorBoundary } from '@/app/components/error-boundary';
import type { SyncResult } from '@/lib/offline/sync-signatures';
import { apiClient } from '@/lib/api-client';
import { filenameFromContentDisposition } from '@/lib/download';
import { enqueueSignature } from '@/lib/offline/outbox';
import { toast } from '@/app/components/ui/simple-toaster';
import { isAxiosError } from 'axios';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { JSAStatus, RiskLevel, NoAccountSigner, SignerDispositionEntry, ExpectedSigner, SignerDisposition, ReDispositionPayload, RescuePlan, PlanRevisionSummary, PlanInitialInput } from '@/lib/types';
import type { SubmitJSAPayload } from '@/hooks/use-safety';
import {
  PlanInitialsFields,
  type PlanInitialRevision,
} from '@/app/components/safety/plan-initials-fields';

function extractErrorMessage(error: Error, fallback: string): string {
  if (isAxiosError<{ error?: string; detail?: string; plan_initials?: string | string[] }>(error)) {
    const data = error.response?.data;
    if (data?.error) return data.error;
    // Server-side rule: initials are only accepted for a plan revision the
    // signer has opened. Surface that message instead of failing silently.
    if (data?.plan_initials) {
      return Array.isArray(data.plan_initials) ? data.plan_initials.join(' ') : data.plan_initials;
    }
    if (data?.detail) return data.detail;
  }
  return fallback;
}

function pdfDownloadFilename(documentType: string, id: number, title: string): string {
  const value = `${documentType}-${id}-${title}`
    .normalize('NFKD')
    .replace(/[^\u0020-\u007E]/g, '')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/^[._-]+|[._-]+$/g, '');
  return `${value || 'document'}.pdf`;
}

function SignedPlanRevisions({
  rescuePlan,
  safeWorkPlan,
}: {
  rescuePlan?: PlanRevisionSummary | null;
  safeWorkPlan?: PlanRevisionSummary | null;
}) {
  if (!rescuePlan && !safeWorkPlan) return null;
  return (
    <div className="mt-2 space-y-1 text-xs text-gray-600">
      {rescuePlan && (
        <p>Signed Rescue Plan revision: {rescuePlan.title} (v{rescuePlan.version})</p>
      )}
      {safeWorkPlan && (
        <p>Signed Safe Work Plan revision: {safeWorkPlan.title} (v{safeWorkPlan.version})</p>
      )}
    </div>
  );
}

const statusColors: Record<JSAStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending_signatures: 'bg-yellow-100 text-yellow-800',
  active: 'bg-green-100 text-green-800',
  closed: 'bg-blue-100 text-blue-800',
  archived: 'bg-gray-100 text-gray-600',
};

const statusBanners: Partial<Record<JSAStatus, { message: string; variant: 'default' | 'destructive' }>> = {
  pending_signatures: { message: 'This JSA is locked for editing — collecting signatures.', variant: 'default' },
  active: { message: 'This JSA is active and in effect. All required signatures have been collected.', variant: 'default' },
  closed: { message: 'This JSA has been closed and archived. No further changes allowed.', variant: 'default' },
  archived: { message: 'This JSA has been archived.', variant: 'default' },
};

const riskColors: Record<RiskLevel, string> = {
  low: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

interface JSAWeatherSnapshot {
  weather_conditions: string;
  wind_speed: string;
  temperature: string;
}

interface JSAHistoryTarget {
  id: string;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
}

const JSA_HISTORY_TOAST_DURATION_MS = 10000;
const JSA_HISTORY_LIMIT = 25;

type SignerDispositionAction =
  | { action: 'submit' }
  | { action: 'reaffirm' | 'resign'; note?: string };

interface RescuePlanPickerProps {
  plans: RescuePlan[];
  jsaProjectId: number | null;
  jsaProjectName: string;
  selectedPlanId: string;
  isLinking: boolean;
  onSelectedPlanChange: (planId: string) => void;
  onLink: () => void;
}

function RescuePlanPicker({
  plans,
  jsaProjectId,
  jsaProjectName,
  selectedPlanId,
  isLinking,
  onSelectedPlanChange,
  onLink,
}: RescuePlanPickerProps) {
  const sameProjectPlans = plans.filter((plan) => plan.project === jsaProjectId);
  const projectlessPlans = plans.filter(
    (plan) => plan.project === null && jsaProjectId !== null
  );
  const otherProjectPlans = plans.filter(
    (plan) => plan.project !== null && plan.project !== jsaProjectId
  );
  const hasProjectScopedPlans =
    sameProjectPlans.length > 0 || projectlessPlans.length > 0;
  const planLabel = (plan: RescuePlan, includeProject = false) => (
    <>
      {plan.title}
      {includeProject
        ? ` — ${plan.project_display?.name || plan.project_name || 'No project'}`
        : ''}
      {' '}({plan.status.replace('_', ' ')})
      {(plan.linked_jsa_ids?.length ?? 0) > 0
        ? ' — already used by another JSA'
        : ''}
    </>
  );

  return (
    <div className="p-4 space-y-2">
      {jsaProjectId !== null && !hasProjectScopedPlans && (
        <p className="text-sm text-amber-700">
          No approved or signed-off plans match {jsaProjectName}.
          Showing approved plans from other projects.
        </p>
      )}
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <Label htmlFor="link-rescue-plan" className="text-sm font-medium">
            Link Rescue Plan
          </Label>
          <Select value={selectedPlanId} onValueChange={onSelectedPlanChange}>
            <SelectTrigger id="link-rescue-plan" className="mt-1">
              <SelectValue placeholder="Select an approved/signed-off rescue plan..." />
            </SelectTrigger>
            <SelectContent>
              {sameProjectPlans.length > 0 && (
                <SelectGroup>
                  <SelectLabel>{jsaProjectId !== null ? 'Same project' : 'No project'}</SelectLabel>
                  {sameProjectPlans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id.toString()}>
                      {planLabel(plan)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
              {projectlessPlans.length > 0 && (
                <SelectGroup>
                  <SelectLabel>Available for any project</SelectLabel>
                  {projectlessPlans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id.toString()}>
                      {planLabel(plan)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
              {otherProjectPlans.length > 0 && (
                <SelectGroup>
                  <SelectLabel>Other projects</SelectLabel>
                  {otherProjectPlans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id.toString()}>
                      {planLabel(plan, true)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={onLink}
          disabled={!selectedPlanId || isLinking}
        >
          {isLinking ? 'Linking...' : 'Link'}
        </Button>
      </div>
    </div>
  );
}

function isEditingText(e: KeyboardEvent): boolean {
  const el = e.target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

export function JSADetailPage() {
  const timeZone = useOrgTimeZone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const jsaId = parseInt(id || '0', 10);
  const { data: jsa, isLoading } = useJSA(jsaId);
  const submitMutation = useSubmitJSAForSignatures();
  const submittingJsaIds = useRef(new Set<number>());
  const currentJsaId = useRef(jsaId);
  const closeMutation = useCloseJSA();
  const activateMutation = useActivateJSA();
  const reaffirmRevalidationMutation = useReaffirmJSARevalidation();
  const addDispatchedCrewMutation = useAddDispatchedCrewToJSA();
  const requireResignMutation = useRequireJSAResign();
  const updateMutation = useUpdateJSA();
  const deleteMutation = useDeleteJSA();
  const signMutation = useSignJSA();
  const { isAdmin, isManager, hasMinModuleRole, canResolveJSARevalidation } = usePermissions();
  const canManageSafety = hasMinModuleRole('safety', 'manager');

  const { user } = useAuth();
  const { branding } = useBranding();
  const pinMode = branding?.kiosk_pin_enabled ?? false;
  const acknowledgeMutation = useAcknowledgeSignJSA();
  const duplicateMutation = useDuplicateJSA();
  const addSignerMutation = useAddExpectedSigner();
  const removeSignerMutation = useRemoveExpectedSigner();
  const reDispositionMutation = useReDispositionSigner();
  const { data: expectedSigners } = useExpectedSigners(jsaId);
  const { data: guestSigners } = useGuestSigners(jsaId);
  const addGuestMutation = useAddGuestSigner();
  const canLinkRescuePlan = Boolean(
    jsa &&
      jsa.project === null &&
      ['draft', 'pending_signatures', 'active'].includes(jsa.status) &&
      canManageSafety
  );
  const {
    data: rescuePlansData,
    dataUpdatedAt: rescuePlansUpdatedAt,
    isLoading: rescuePlansLoading,
    isError: rescuePlansError,
  } = useRescuePlans(
    {
      linkable_to_jsa: 'true',
      page_size: 1000,
    },
    canLinkRescuePlan
  );
  const linkRescuePlanMutation = useLinkRescuePlanToJSA();
  const queryClient = useQueryClient();

  useEffect(() => {
    currentJsaId.current = jsaId;
  }, [jsaId]);

  const [signerName, setSignerName] = useState('');
  const [signerRole, setSignerRole] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [showSignForm, setShowSignForm] = useState(false);
  const [showDrawSignature, setShowDrawSignature] = useState(false);
  const [acknowledgementData, setAcknowledgementData] = useState<HazardAcknowledgement[] | null>(null);
  const [planInitials, setPlanInitials] = useState<PlanInitialInput[]>([]);
  const [downloading, setDownloading] = useState(false);
  const [acknowledgeSuccess, setAcknowledgeSuccess] = useState<{ name: string; role: string; time: string } | null>(null);
  const [showSigningQR, setShowSigningQR] = useState(false);
  const [showCrewSearch, setShowCrewSearch] = useState(false);
  const [crewSearchQuery, setCrewSearchQuery] = useState('');
  const [weatherConditions, setWeatherConditions] = useState('');
  const [windSpeed, setWindSpeed] = useState('');
  const [temperature, setTemperature] = useState('');
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestQRToken, setGuestQRToken] = useState<string | null>(null);
  const [kioskLineId, setKioskLineId] = useState<number | null>(null);
  const [kioskSigs, setKioskSigs] = useState<Record<number, { signer_name: string; signed_at: string; queued?: boolean }>>({});

  const handleOfflineSync = useCallback(
    (result: SyncResult) => {
      // Clear any optimistic per-row kiosk state after a sync attempt so failed
      // or rejected signatures (e.g. wrong row) don't continue to show as queued.
      setKioskSigs({});

      if (result.jsaIds.includes(jsaId)) {
        queryClient.invalidateQueries({ queryKey: queryKeys.jsas.detail(jsaId) });
        queryClient.invalidateQueries({ queryKey: [...queryKeys.jsas.detail(jsaId), 'expected-signers'] });
        queryClient.invalidateQueries({ queryKey: queryKeys.jsas.lists() });
      }
    },
    [jsaId, queryClient, setKioskSigs],
  );

  const [showRescuePlanOverride, setShowRescuePlanOverride] = useState(false);
  const [rescuePlanWarningMsg, setRescuePlanWarningMsg] = useState('');
  const [submissionBlockReason, setSubmissionBlockReason] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideReasonError, setOverrideReasonError] = useState<string | null>(null);
  const [selectedRescuePlanId, setSelectedRescuePlanId] = useState<string>('');
  const [showSignerDisposition, setShowSignerDisposition] = useState(false);
  const [noAccountSigners, setNoAccountSigners] = useState<NoAccountSigner[]>([]);
  const [signerDispositions, setSignerDispositions] = useState<SignerDispositionEntry[]>([]);
  const [signerDispositionAction, setSignerDispositionAction] =
    useState<SignerDispositionAction>({ action: 'submit' });
  const [showReDisposition, setShowReDisposition] = useState(false);
  const [reDispositionSigner, setReDispositionSigner] = useState<ExpectedSigner | null>(null);
  const [reDispositionValue, setReDispositionValue] = useState<SignerDisposition | ''>('');
  const [reDispositionReason, setReDispositionReason] = useState('');
  const jsaUndoStackRef = useRef<JSAHistoryTarget[]>([]);
  const jsaRedoStackRef = useRef<JSAHistoryTarget[]>([]);
  const jsaUndoToastIdRef = useRef<string | number | null>(null);

  const queueSignature = useCallback(async (body: Record<string, unknown>, successMessage: string) => {
    if (!jsa) {
      return false;
    }
    await enqueueSignature({
      jsaId: jsa.id,
      endpoint: `/api/v1/safety/jsas/${jsa.id}/sign/`,
      body,
      capturedAt: Date.now(),
    });
    toast.success(successMessage);
    return true;
  }, [jsa]);

  const applyWeatherSnapshot = useCallback(async (snapshot: JSAWeatherSnapshot) => {
    await updateMutation.mutateAsync({ id: jsaId, data: snapshot });
    setWeatherConditions(snapshot.weather_conditions);
    setWindSpeed(snapshot.wind_speed);
    setTemperature(snapshot.temperature);
  }, [jsaId, updateMutation]);

  const runJSAHistory = useCallback(async (direction: 'undo' | 'redo') => {
    const source = direction === 'undo' ? jsaUndoStackRef.current : jsaRedoStackRef.current;
    const target = source.pop();
    if (!target) {
      toast.error(direction === 'undo' ? 'Nothing to undo.' : 'Nothing to redo.');
      return;
    }

    const destination = direction === 'undo' ? jsaRedoStackRef.current : jsaUndoStackRef.current;
    const promise = target[direction]().then(
      () => {
        destination.push(target);
        if (destination.length > JSA_HISTORY_LIMIT) {
          destination.shift();
        }
      },
      (error) => {
        // Restore the target to the source stack if the operation failed so the
        // undo/redo history stays in sync.
        source.push(target);
        throw error;
      }
    );

    await toast.promise(promise, {
      loading: direction === 'undo' ? 'Undoing JSA edit…' : 'Redoing JSA edit…',
      success: direction === 'undo' ? 'JSA edit undone' : 'JSA edit redone',
      error: direction === 'undo' ? 'Failed to undo JSA edit' : 'Failed to redo JSA edit',
    });
  }, []);

  const showJSAWeatherUndo = useCallback((previous: JSAWeatherSnapshot, next: JSAWeatherSnapshot) => {
    const historyTarget: JSAHistoryTarget = {
      id: `${Date.now()}-${Math.random()}`,
      undo: () => applyWeatherSnapshot(previous),
      redo: () => applyWeatherSnapshot(next),
    };
    jsaUndoStackRef.current.push(historyTarget);
    if (jsaUndoStackRef.current.length > JSA_HISTORY_LIMIT) {
      jsaUndoStackRef.current.shift();
    }
    jsaRedoStackRef.current = [];

    if (jsaUndoToastIdRef.current !== null) {
      toast.dismiss(jsaUndoToastIdRef.current);
    }

    const toastId = toast.success('JSA weather saved', {
      description: `${jsaUndoStackRef.current.length} undoable JSA edit${jsaUndoStackRef.current.length === 1 ? '' : 's'}. Press Ctrl+Z to step back.`,
      duration: JSA_HISTORY_TOAST_DURATION_MS,
      action: {
        label: 'Undo',
        onClick: () => runJSAHistory('undo'),
      },
    });
    jsaUndoToastIdRef.current = toastId;
  }, [applyWeatherSnapshot, runJSAHistory]);

  useEffect(() => {
    if (jsa) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setWeatherConditions(jsa.weather_conditions || '');
      setWindSpeed(jsa.wind_speed || '');
      setTemperature(jsa.temperature || '');
    }
  }, [jsa?.id]);

  const { data: signingQRData, isLoading: signingQRLoading } = useJSASigningQR(
    jsaId,
    showSigningQR && jsa?.status === 'pending_signatures'
  );
  const crewSearchEnabled = showCrewSearch && crewSearchQuery.length >= 2;
  const { data: personResults } = usePersons(
    crewSearchEnabled ? { q: crewSearchQuery, page_size: 10 } : {},
    crewSearchEnabled
  );

  const canSign = jsa && (jsa.status === 'pending_signatures' || jsa.status === 'active');
  const hasCurrentUserSigned = jsa && user ? (
    jsa.signatures.some((sig) => sig.signer_user_id === user.id) ||
    (expectedSigners || []).some((s) => s.user_id === user.id && s.has_signed)
  ) : false;
  const isEditable = jsa?.status === 'draft';
  const isLocked = jsa && jsa.status !== 'draft';

  const buildGuestSignUrl = (token: string) =>
    `${window.location.origin}/jsa-guest-sign/${token}`;

  const handleCopyGuestLink = (token: string) => {
    navigator.clipboard.writeText(buildGuestSignUrl(token));
    toast.success('Link copied', {
      description: 'Guest signing link has been copied to clipboard.',
    });
  };

  useEffect(() => {
    jsaUndoStackRef.current = [];
    jsaRedoStackRef.current = [];
    if (jsaUndoToastIdRef.current !== null) {
      toast.dismiss(jsaUndoToastIdRef.current);
      jsaUndoToastIdRef.current = null;
    }
  }, [jsaId]);

  useEffect(() => {
    if (isEditable === false) {
      jsaUndoStackRef.current = [];
      jsaRedoStackRef.current = [];
      if (jsaUndoToastIdRef.current !== null) {
        toast.dismiss(jsaUndoToastIdRef.current);
        jsaUndoToastIdRef.current = null;
      }
    }
  }, [isEditable]);

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      const modifier = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      const dialogOpen = showDiscardConfirm || showDuplicateConfirm || showSignForm ||
        showDrawSignature || showSigningQR || showCrewSearch || showGuestForm || guestQRToken !== null || kioskLineId !== null;

      if (!isEditable || !modifier || e.altKey || dialogOpen || isEditingText(e)) return;

      if (key === 'z') {
        e.preventDefault();
        runJSAHistory(e.shiftKey ? 'redo' : 'undo');
        return;
      }

      if (key === 'y' && !e.shiftKey) {
        e.preventDefault();
        runJSAHistory('redo');
      }
    }

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    isEditable,
    kioskLineId,
    runJSAHistory,
    showCrewSearch,
    showDiscardConfirm,
    showDrawSignature,
    showDuplicateConfirm,
    guestQRToken,
    showGuestForm,
    showSignForm,
    showSigningQR,
  ]);

  const handleDownloadPdf = async () => {
    if (!jsa) return;
    setDownloading(true);
    try {
      const response = await apiClient.get(`/api/v1/safety/jsas/${jsa.id}/pdf/`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        filenameFromContentDisposition(
          response.headers['content-disposition'],
          pdfDownloadFilename('JSA', jsa.id, jsa.title),
        ),
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(extractErrorMessage(error as Error, 'Failed to download JSA PDF.'));
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadPackagePdf = async () => {
    if (!jsa) return;
    setDownloading(true);
    try {
      const response = await apiClient.get(`/api/v1/safety/jsas/${jsa.id}/safety-package-pdf/`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        filenameFromContentDisposition(
          response.headers['content-disposition'],
          pdfDownloadFilename('SafetyPackage', jsa.id, jsa.title),
        ),
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(extractErrorMessage(error as Error, 'Failed to download safety package PDF.'));
    } finally {
      setDownloading(false);
    }
  };

  const handleSignatureAccept = async (dataUrl: string) => {
    if (!jsa || !signerName || !signerRole || !acknowledgementData) return;
    if (!navigator.onLine) {
      const queued = await queueSignature(
        {
          signer_name: signerName,
          signer_role: signerRole,
          signer_email: signerEmail || undefined,
          signature_data: dataUrl,
          acknowledgement_data: acknowledgementData,
          plan_initials: planInitials,
        },
        'Signature queued — will sync when you\'re back online'
      );
      if (queued) {
        setSignerName('');
        setSignerRole('');
        setSignerEmail('');
        setAcknowledgementData(null);
        setPlanInitials([]);
        setShowSignForm(false);
        setShowDrawSignature(false);
      }
      return;
    }
    signMutation.mutate(
      {
        id: jsa.id,
        data: {
          signer_name: signerName,
          signer_role: signerRole,
          signer_email: signerEmail || undefined,
          signature_data: dataUrl,
          acknowledgement_data: acknowledgementData,
          plan_initials: planInitials,
        },
      },
      {
        onSuccess: () => {
          setSignerName('');
          setSignerRole('');
          setSignerEmail('');
          setAcknowledgementData(null);
          setPlanInitials([]);
          setShowSignForm(false);
          setShowDrawSignature(false);
        },
        onError: (error) => {
          toast.error(extractErrorMessage(error, 'Failed to record signature.'));
        },
      }
    );
  };

  const handleAcknowledgeSign = async () => {
    if (!jsa || !acknowledgementData) return;
    if (!navigator.onLine) {
      const queued = await queueSignature(
        {
          acknowledgement_data: acknowledgementData,
          plan_initials: planInitials,
        },
        'Signature queued — will sync when you\'re back online'
      );
      if (queued) {
        setAcknowledgementData(null);
        setPlanInitials([]);
        setShowSignForm(false);
        setShowDrawSignature(false);
      }
      return;
    }
    acknowledgeMutation.mutate(
      {
        id: jsa.id,
        acknowledgement_data: acknowledgementData,
        plan_initials: planInitials,
      },
      {
        onSuccess: (data) => {
          // The just-created signature belongs to the current user, not
          // necessarily signatures[0] (ordering may vary).
          const mySig = user
            ? data.signatures.find((sig) => sig.signer_user_id === user.id)
            : undefined;
          const latestSig = mySig ?? data.signatures[0];
          if (latestSig) {
            setAcknowledgeSuccess({
              name: latestSig.signer_name,
              role: latestSig.signer_role,
              time: formatDateTimeInTimeZone(latestSig.signed_at, timeZone),
            });
          }
          setAcknowledgementData(null);
          setPlanInitials([]);
          setShowSignForm(false);
          setShowDrawSignature(false);
        },
        onError: (error) => {
          toast.error(extractErrorMessage(error, 'Failed to record acknowledgement.'));
        },
      }
    );
  };

  const openSignerDispositionDialog = (
    error: Error,
    action: SignerDispositionAction,
  ): boolean => {
    if (
      isAxiosError<{
        no_account_signers_required?: boolean;
        no_account_signers?: NoAccountSigner[];
      }>(error) &&
      error.response?.status === 409 &&
      error.response.data?.no_account_signers_required
    ) {
      setNoAccountSigners(error.response.data.no_account_signers || []);
      setSignerDispositions([]);
      setSignerDispositionAction(action);
      setShowSignerDisposition(true);
      return true;
    }
    return false;
  };

  const handleSubmitForSignatures = async (
    extraPayload: Record<string, unknown> = {},
  ) => {
    if (!jsa || submittingJsaIds.current.has(jsa.id)) return;
    const submittedJsaId = jsa.id;
    submittingJsaIds.current.add(submittedJsaId);
    setSubmissionBlockReason('');
    try {
      await submitMutation.mutateAsync({ id: submittedJsaId, ...extraPayload });
      if (currentJsaId.current === submittedJsaId) {
        setSubmissionBlockReason('');
      }
    } catch (error) {
      if (currentJsaId.current !== submittedJsaId) return;
      if (openSignerDispositionDialog(error as Error, { action: 'submit' })) {
        return;
      }
      if (
        isAxiosError<{
          rescue_plan_warning?: boolean;
          message?: string;
        }>(error) &&
        error.response?.status === 409 &&
        error.response.data?.rescue_plan_warning
      ) {
        setRescuePlanWarningMsg(
          error.response.data.message ||
            'Linked rescue plan(s) have not been signed off.',
        );
        setShowRescuePlanOverride(true);
        return;
      }
      const message = extractErrorMessage(
        error as Error,
        'Failed to submit JSA for signatures.',
      );
      setSubmissionBlockReason(message);
      toast.error(message);
    } finally {
      submittingJsaIds.current.delete(submittedJsaId);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!jsa) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">JSA not found.</p>
        <Button asChild className="mt-4">
          <Link to="/safety/jsas">Back to JSAs</Link>
        </Button>
      </div>
    );
  }

  const activeRevalidation = jsa.active_revalidation;

  const linkedRescuePlanIds = new Set(jsa.linked_rescue_plans?.map((rp) => rp.id) ?? []);
  const candidateRescuePlans = rescuePlansData?.results.filter(
    (rp) => !linkedRescuePlanIds.has(rp.id)
  ) ?? [];
  const rescuePlanRevision = jsa.rescue_plan_revision;
  const rescuePlanIsSigned =
    rescuePlanRevision?.status === 'approved' ||
    rescuePlanRevision?.status === 'signed_off';
  const signedRescuePlanExists = jsa.project !== null
    ? rescuePlanIsSigned
    : (jsa.linked_rescue_plans ?? []).some(
        (plan) => plan.status === 'approved' || plan.status === 'signed_off',
      );
  const unsignedLinkedRescuePlan = jsa.project === null
    ? jsa.linked_rescue_plans?.find(
        (plan) => plan.status !== 'approved' && plan.status !== 'signed_off'
      )
    : null;
  const rescuePlanRemediationHref = canManageSafety
    ? jsa.project !== null
      ? rescuePlanRevision && !rescuePlanIsSigned
        ? `/safety/rescue-plans/${rescuePlanRevision.id}`
        : !rescuePlanRevision
          ? `/safety/rescue-plans/new?from_jsa=${jsa.id}`
          : null
      : unsignedLinkedRescuePlan
        ? `/safety/rescue-plans/${unsignedLinkedRescuePlan.id}`
        : candidateRescuePlans.length > 0
          ? '#linked-rescue-plans'
          : `/safety/rescue-plans/new?from_jsa=${jsa.id}`
    : null;
  const rescuePlanRemediationLabel = jsa.project !== null
    ? rescuePlanRevision
      ? rescuePlanRevision.status === 'pending_review'
        ? 'Approve rescue plan'
        : rescuePlanRevision.status === 'draft'
          ? 'Submit rescue plan for review'
          : 'Sign off rescue plan'
      : 'Create rescue plan for this job'
    : unsignedLinkedRescuePlan
      ? unsignedLinkedRescuePlan.status === 'pending_review'
        ? 'Approve rescue plan'
        : 'Submit rescue plan for review'
      : candidateRescuePlans.length > 0
        ? 'Link a rescue plan'
        : 'Create rescue plan';
  const signedGuests = (guestSigners || []).filter((g) => g.has_signed);
  const requiredSignaturesCollected =
    expectedSigners !== undefined &&
    expectedSigners
      .filter((signer) => signer.requires_signature !== false)
      .every((signer) => signer.has_signed);
  const hasCollectedSignature = jsa.signature_count > 0 || signedGuests.length > 0;
  const noRescuePlanLinked = jsa.project !== null
    ? !rescuePlanRevision
    : (jsa.linked_rescue_plans ?? []).length === 0;
  // Mirrors the server rule: a JSA submitted without a rescue plan (with a
  // reason, only allowed when the org makes rescue plans optional) may activate.
  const rescuePlanGatePasses =
    signedRescuePlanExists || (noRescuePlanLinked && Boolean(jsa.rescue_plan_override_reason));
  const canActivateJSA =
    requiredSignaturesCollected && hasCollectedSignature && rescuePlanGatePasses;
  const signPlans: PlanInitialRevision[] = [];
  if (jsa.safe_work_plan_revision && ['approved', 'active'].includes(jsa.safe_work_plan_revision.status)) {
    signPlans.push({
      plan_type: 'safe_work_plan',
      id: jsa.safe_work_plan_revision.id,
      title: jsa.safe_work_plan_revision.title,
      version: jsa.safe_work_plan_revision.version,
      url: `/safety/work-plans/${jsa.safe_work_plan_revision.id}`,
    });
  }
  if (jsa.rescue_plan_revision && ['signed_off', 'approved', 'active'].includes(jsa.rescue_plan_revision.status)) {
    signPlans.push({
      plan_type: 'rescue_plan',
      id: jsa.rescue_plan_revision.id,
      title: jsa.rescue_plan_revision.title,
      version: jsa.rescue_plan_revision.version,
      url: `/safety/rescue-plans/${jsa.rescue_plan_revision.id}`,
    });
  }
  const plansForPerson = (personId?: number | null) =>
    signPlans.map((plan) => {
      const summary = jsa.crew_plan_initials?.find(
        (item) =>
          item.plan_type === plan.plan_type
          && item.plan_revision_id === plan.id,
      );
      const existing = summary?.initialled.find(
        (item) => item.person_id === personId,
      );
      return {
        ...plan,
        initialled_at: existing?.initialled_at,
      };
    });

  let banner = statusBanners[jsa.status];
  if (jsa.status === 'active') {
    const requiredExpected = (expectedSigners || []).filter(
      (s) => s.requires_signature !== false && s.disposition !== 'advisory'
    ).length;
    const linkedGuestIds = new Set(
      (expectedSigners || [])
        .map((s) => s.guest_signer_id)
        .filter((id): id is number => id != null)
    );
    const standaloneGuests = (guestSigners || []).filter((g) => !linkedGuestIds.has(g.id));
    const required = requiredExpected + standaloneGuests.length;
    const signed =
      (expectedSigners || []).filter(
        (s) => s.requires_signature !== false && s.disposition !== 'advisory' && s.has_signed
      ).length +
      standaloneGuests.filter((g) => g.has_signed).length;
    if (required > 0 && signed < required) {
      banner = {
        message: `This JSA is active and in effect. ${signed}/${required} signature${required === 1 ? '' : 's'} collected.`,
        variant: 'default',
      };
    } else {
      banner = {
        message: 'This JSA is active and in effect. All required signatures have been collected.',
        variant: 'default',
      };
    }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/safety/jsas')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{jsa.title}</h1>
            <p className="text-gray-600">
              {jsa.location} &middot; {formatDateOnly(jsa.date)}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
          <Badge className={statusColors[jsa.status]}>{jsa.status.replace('_', ' ')}</Badge>
          {isLocked && <Lock className="h-4 w-4 text-gray-400" />}
          {canManageSafety && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDuplicateConfirm(true)}
              disabled={duplicateMutation.isPending}
            >
              <Copy className="h-4 w-4 mr-1" />
              {duplicateMutation.isPending ? 'Duplicating...' : 'Duplicate'}
            </Button>
          )}
          {jsa.status === 'pending_signatures' && (
            <Button variant="outline" size="sm" onClick={() => setShowSigningQR(true)}>
              <QrCode className="h-4 w-4 mr-1" />
              Signing QR
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleDownloadPdf} disabled={downloading}>
            <FileDown className="h-4 w-4 mr-1" />
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadPackagePdf} disabled={downloading}>
            <Package className="h-4 w-4 mr-1" />
            Safety Package
          </Button>
        </div>
      </div>

      {jsa.status !== 'active' && jsa.plan_changed_requires_rebrief && (
        <Alert className="border-amber-300 bg-amber-50 text-amber-950 [&>svg]:text-amber-600">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Plan changed: re-brief crew</AlertTitle>
          <AlertDescription>
            The job plan has changed since this JSA was sent for signatures.
          </AlertDescription>
        </Alert>
      )}

      {banner && (
        <Alert variant={banner.variant}>
          <AlertDescription>{banner.message}</AlertDescription>
        </Alert>
      )}

      {(jsa.crew_plan_initial_warnings ?? []).map((warning) => (
        <Alert
          key={`${warning.plan_type}-${warning.plan_revision_id}`}
          className="border-amber-300 bg-amber-50 text-amber-900"
        >
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{warning.message}</AlertDescription>
        </Alert>
      ))}

      {jsa.rescue_plan_warning && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>This JSA is active but does not have a signed rescue plan for its job.</span>
            {rescuePlanRemediationHref ? (
              <Button variant="outline" size="sm" asChild>
                <Link to={rescuePlanRemediationHref}>{rescuePlanRemediationLabel}</Link>
              </Button>
            ) : (
              <span className="text-sm">
                {jsa.project !== null
                  ? "Ask an admin or manager to create or sign off the job's rescue plan."
                  : 'Ask an admin or manager to link a signed rescue plan.'}
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      <PdfOfflineNotice />

      <Card>
        <CardHeader>
          <CardTitle>Project Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Project:</span>{' '}
              {jsa.project_display ? (
                <Link
                  to={`/projects/${jsa.project_display.id}`}
                  className="text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  {jsa.project_display.name}
                  <ExternalLink className="h-3 w-3" />
                </Link>
              ) : (
                jsa.project_name || 'N/A'
              )}
            </div>
            <div>
              <span className="text-gray-500">Project #:</span>{' '}
              {jsa.project_display?.project_number || jsa.project_number || 'N/A'}
            </div>
            <div>
              <span className="text-gray-500">Created By:</span>{' '}
              {jsa.created_by_username}
            </div>
            <div>
              <span className="text-gray-500">Created:</span>{' '}
              {formatDateTimeInTimeZone(jsa.created_at, timeZone, { dateStyle: 'medium' })}
            </div>
          </div>
          {jsa.scope_of_work && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Scope of Work</p>
              <p className="text-sm whitespace-pre-wrap">{jsa.scope_of_work}</p>
            </div>
          )}
          {isEditable ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Weather Conditions</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="weather-conditions" className="text-xs text-gray-500">Conditions</Label>
                  <Input
                    id="weather-conditions"
                    value={weatherConditions}
                    onChange={(e) => setWeatherConditions(e.target.value)}
                    placeholder="e.g. Partly cloudy"
                  />
                </div>
                <div>
                  <Label htmlFor="wind-speed" className="text-xs text-gray-500">Wind Speed</Label>
                  <Input
                    id="wind-speed"
                    value={windSpeed}
                    onChange={(e) => setWindSpeed(e.target.value)}
                    placeholder="e.g. 15 km/h"
                  />
                </div>
                <div>
                  <Label htmlFor="temperature" className="text-xs text-gray-500">Temperature</Label>
                  <Input
                    id="temperature"
                    value={temperature}
                    onChange={(e) => setTemperature(e.target.value)}
                    placeholder="e.g. 18°C"
                  />
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const previous = {
                    weather_conditions: jsa.weather_conditions || '',
                    wind_speed: jsa.wind_speed || '',
                    temperature: jsa.temperature || '',
                  };
                  const next = {
                    weather_conditions: weatherConditions,
                    wind_speed: windSpeed,
                    temperature,
                  };
                  const hasChanges = (
                    next.weather_conditions !== previous.weather_conditions ||
                    next.wind_speed !== previous.wind_speed ||
                    next.temperature !== previous.temperature
                  );

                  updateMutation.mutate({
                    id: jsa.id,
                    data: next,
                  }, {
                    onSuccess: () => {
                      if (hasChanges) showJSAWeatherUndo(previous, next);
                    },
                    onError: (error) => {
                      toast.error(extractErrorMessage(error, 'Failed to save weather.'));
                    },
                  });
                }}
                disabled={updateMutation.isPending}
              >
                Save Weather
              </Button>
            </div>
          ) : (jsa.weather_conditions && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Weather</p>
              <p className="text-sm">
                {jsa.weather_conditions}
                {jsa.wind_speed && ` | Wind: ${jsa.wind_speed}`}
                {jsa.temperature && ` | Temp: ${jsa.temperature}`}
              </p>
            </div>
          ))}
        </CardContent>
      </Card>

      {jsa.hazards.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Hazard Identification</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* Desktop: standard table */}
            <div className="hidden sm:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Task</TableHead>
                    <TableHead>Hazard</TableHead>
                    <TableHead>Risk</TableHead>
                    <TableHead>Controls</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jsa.hazards.map((hazard) => (
                    <TableRow key={hazard.id}>
                      <TableCell className="font-medium">{hazard.step_number}</TableCell>
                      <TableCell>{hazard.task_description}</TableCell>
                      <TableCell>{hazard.hazard_description}</TableCell>
                      <TableCell>
                        <Badge className={riskColors[hazard.risk_level]}>
                          {hazard.risk_level}
                        </Badge>
                      </TableCell>
                      <TableCell>{hazard.control_measures}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile: stacked cards so Controls is always visible */}
            <div className="sm:hidden space-y-3 p-4">
              {jsa.hazards.map((hazard) => (
                <div key={hazard.id} className="rounded-lg border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">#{hazard.step_number}</span>
                    <Badge className={riskColors[hazard.risk_level]}>
                      {hazard.risk_level}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Task</p>
                    <p className="text-sm">{hazard.task_description}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Hazard</p>
                    <p className="text-sm">{hazard.hazard_description}</p>
                  </div>
                  <div className="border-t pt-2">
                    <p className="text-xs font-medium text-muted-foreground">Controls</p>
                    <p className="text-sm">{hazard.control_measures}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {(jsa.ppe_requirements || jsa.emergency_procedures) && (
        <Card>
          <CardHeader>
            <CardTitle>Safety Procedures</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {jsa.ppe_requirements && (
              <div>
                <p className="text-sm text-gray-500 mb-1">PPE Requirements</p>
                <p className="text-sm whitespace-pre-wrap">{jsa.ppe_requirements}</p>
              </div>
            )}
            {jsa.emergency_procedures && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Emergency Procedures</p>
                <p className="text-sm whitespace-pre-wrap">{jsa.emergency_procedures}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Crew / Expected Signers (editable for draft) */}
      {isEditable && (expectedSigners && expectedSigners.length > 0 || true) && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              Crew (Expected Signers)
              {expectedSigners && <Badge variant="outline">{expectedSigners.length} members</Badge>}
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => setShowCrewSearch(!showCrewSearch)}>
              <UserPlus className="h-4 w-4 mr-1" />
              Add Crew
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {showCrewSearch && (
              <div className="border rounded-md p-3 space-y-2 bg-gray-50">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Search crew by name..."
                    value={crewSearchQuery}
                    onChange={(e) => setCrewSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                {crewSearchQuery.length >= 2 && personResults?.results && personResults.results.length > 0 && (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {personResults.results.map((person) => {
                      const alreadyAdded = expectedSigners?.some((s) => s.tech_id === person.id);
                      return (
                        <button
                          key={person.id}
                          className="w-full flex items-center justify-between p-2 text-sm rounded hover:bg-gray-100 disabled:opacity-50"
                          disabled={alreadyAdded || addSignerMutation.isPending}
                          onClick={() => {
                            addSignerMutation.mutate({ jsaId, techId: person.id }, {
                              onSuccess: () => setCrewSearchQuery(''),
                            });
                          }}
                        >
                          <span>{person.full_name}</span>
                          {alreadyAdded ? (
                            <Badge variant="outline" className="text-xs">Added</Badge>
                          ) : (
                            <Plus className="h-4 w-4 text-blue-600" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                {crewSearchQuery.length >= 2 && personResults?.results && personResults.results.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-2">No people found.</p>
                )}
              </div>
            )}
            {expectedSigners && expectedSigners.length > 0 ? (
              <div className="space-y-2">
                {expectedSigners.map((signer) => (
                  <div key={signer.tech_id} className="flex items-center gap-3 p-2 rounded-md border text-sm">
                    {signer.is_creator ? (
                      <UserCheck className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                    ) : (
                      <UserPlus className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <span className="font-medium">{signer.name}</span>
                      {signer.is_creator ? (
                        <Badge className="ml-2 text-xs bg-indigo-100 text-indigo-700 border-indigo-200">Creator</Badge>
                      ) : (
                        signer.role && <span className="text-gray-500 ml-2">({signer.role})</span>
                      )}
                    </div>
                    {!signer.is_creator && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSignerMutation.mutate({ jsaId, techId: signer.tech_id })}
                        disabled={removeSignerMutation.isPending}
                        title="Remove from crew"
                      >
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No expected signers yet. Add crew members above.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Signing Progress (for non-draft) */}
      {!isEditable && (jsa.expected_signer_count > 0 || (expectedSigners && expectedSigners.length > 0)) && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              Signing Progress
              <Badge variant="outline">{jsa.signing_progress} crew members signed</Badge>
            </CardTitle>
            {jsa.status === 'pending_signatures' && (
              <Button size="sm" variant="outline" onClick={() => setShowSigningQR(true)}>
                <QrCode className="h-4 w-4 mr-1" />
                Signing QR
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {expectedSigners && expectedSigners.length > 0 && (
              <div className="space-y-2">
                {expectedSigners.map((signer) => {
                  const sigMatch = jsa.signatures.find(
                    (sig) =>
                      (signer.user_id != null && sig.signer_user_id === signer.user_id) ||
                      (signer.name && sig.signer_name === signer.name)
                  );
                  const signed = signer.has_signed || sigMatch != null;
                  const signedAt = signer.signed_at ?? sigMatch?.signed_at ?? null;
                  return (
                  <div key={signer.tech_id} className="flex items-center gap-3 p-2 rounded-md border text-sm">
                    {signed ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                    ) : (
                      <Clock className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <span className="font-medium">{signer.name}</span>
                      {signer.is_creator ? (
                        <Badge className="ml-2 text-xs bg-indigo-100 text-indigo-700 border-indigo-200">Creator</Badge>
                      ) : (
                        signer.role && <span className="text-gray-500 ml-2">({signer.role})</span>
                      )}
                    </div>
                    {signed && signedAt && (
                      <span className="text-xs text-gray-400">{formatDateTimeInTimeZone(signedAt, timeZone)}</span>
                    )}
                    {!signed && signer.id != null && kioskSigs[signer.id] && (
                      kioskSigs[signer.id!].queued ? (
                        <>
                          <span className="text-sm text-amber-700 font-medium">{kioskSigs[signer.id!].signer_name}</span>
                          <Badge variant="outline" className="text-amber-600 border-amber-200">
                            <Clock className="h-3 w-3 mr-1" />
                            Queued — will sync when reconnected
                          </Badge>
                        </>
                      ) : (
                        <>
                          <span className="text-sm text-green-700 font-medium">{kioskSigs[signer.id!].signer_name}</span>
                          <span className="text-xs text-gray-400">{formatDateTimeInTimeZone(kioskSigs[signer.id!].signed_at, timeZone)}</span>
                        </>
                      )
                    )}
                    {!signed && (signer.id == null || !kioskSigs[signer.id]) && canSign && signer.id != null && (
                      <>
                        {signer.has_user_account !== false && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setKioskLineId(signer.id!)}
                          >
                            <PenLine className="h-3 w-3 mr-1" />
                            Sign Here
                          </Button>
                        )}
                        {signer.has_user_account === false && (isAdmin || isManager) && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setReDispositionSigner(signer);
                                setReDispositionValue((signer.disposition as SignerDisposition) || 'invited');
                                setReDispositionReason(signer.advisory_reason || '');
                                setShowReDisposition(true);
                              }}
                            >
                              Change disposition
                            </Button>
                            {(() => {
                              const guest = guestSigners?.find((g) => g.id === signer.guest_signer_id);
                              if (!guest || guest.has_signed || guest.is_expired) return null;
                              return (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2"
                                    onClick={() => handleCopyGuestLink(guest.token!)}
                                    title="Copy signing link"
                                  >
                                    <Link2 className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2"
                                    onClick={() => setGuestQRToken(guest.token!)}
                                    title="Show QR code"
                                  >
                                    <QrCode className="h-3.5 w-3.5" />
                                  </Button>
                                </>
                              );
                            })()}
                          </>
                        )}
                      </>
                    )}
                    {!signed && (signer.id == null || !kioskSigs[signer.id]) && (!canSign || signer.id == null) && (
                      <Badge variant="outline" className="text-yellow-600">Pending</Badge>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
            {/* Guest signers (only standalone guests; kiosk-linked expected signers are shown above) */}
            {(() => {
              const linkedGuestIds = new Set(
                (expectedSigners || [])
                  .map((s) => s.guest_signer_id)
                  .filter((id): id is number => id != null)
              );
              const standaloneGuests = (guestSigners || []).filter((g) => !linkedGuestIds.has(g.id));
              if (standaloneGuests.length === 0) return null;
              return (
              <div className="mt-3 pt-3 border-t space-y-2">
                <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                  <Mail className="h-3 w-3" /> Guest signers
                </p>
                {standaloneGuests.map((guest) => (
                  <div key={guest.id} className="flex items-center gap-3 p-2 rounded-md border text-sm">
                    {guest.has_signed ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                    ) : (
                      <Clock className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="font-medium">{guest.name || guest.email}</span>
                      <span className="text-gray-500 ml-2 text-xs">{guest.email}</span>
                      <Badge className="ml-2 text-xs bg-purple-100 text-purple-700 border-purple-200">Guest</Badge>
                    </div>
                    {guest.has_signed && guest.signed_at && (
                      <span className="text-xs text-gray-400">{formatDateTimeInTimeZone(guest.signed_at, timeZone)}</span>
                    )}
                    {!guest.has_signed && guest.is_expired && (
                      <Badge variant="outline" className="text-red-500 text-xs">Expired</Badge>
                    )}
                    {!guest.has_signed && !guest.is_expired && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {guest.token && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2"
                              onClick={() => handleCopyGuestLink(guest.token!)}
                              title="Copy signing link"
                            >
                              <Link2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2"
                              onClick={() => setGuestQRToken(guest.token!)}
                              title="Show QR code"
                            >
                              <QrCode className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        <Badge variant="outline" className="text-yellow-600">Pending</Badge>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              );
            })()}
            {/* Walk-up signers: people who signed but aren't in the expected list */}
            {(() => {
              const expectedUserIds = new Set((expectedSigners || []).map((s) => s.user_id).filter(Boolean));
              const expectedNames = new Set((expectedSigners || []).filter((s) => !s.user_id && s.name).map((s) => s.name));
              const walkUpSigs = jsa.signatures.filter(
                (sig) => sig.signer_user_id && !expectedUserIds.has(sig.signer_user_id) && !expectedNames.has(sig.signer_name)
              );
              if (walkUpSigs.length === 0) return null;
              return (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                    <UserPlus className="h-3 w-3" /> Walk-up signers (not assigned to this project)
                  </p>
                  {walkUpSigs.map((sig) => (
                    <div key={sig.id} className="flex items-center gap-3 p-2 rounded-md border text-sm mb-1">
                      <CheckCircle2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <div className="flex-1">
                        <span className="font-medium">{sig.signer_name}</span>
                        {sig.signer_role && <span className="text-gray-500 ml-2">({sig.signer_role})</span>}
                      </div>
                      <span className="text-xs text-gray-400">{formatDateTimeInTimeZone(sig.signed_at, timeZone)}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Add Guest Signer (admin/manager, pending_signatures only) */}
      {!isEditable && jsa.status === 'pending_signatures' && (isAdmin || isManager) && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Guest Signers
            </CardTitle>
            {!showGuestForm && (
              <Button size="sm" variant="outline" onClick={() => setShowGuestForm(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Add Guest
              </Button>
            )}
          </CardHeader>
          {showGuestForm && (
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="guest-email">Email <span className="text-red-500">*</span></Label>
                <Input
                  id="guest-email"
                  type="email"
                  placeholder="guest@example.com"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="guest-name">Name (optional)</Label>
                <Input
                  id="guest-name"
                  placeholder="Full name"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="guest-phone">Phone (optional)</Label>
                <Input
                  id="guest-phone"
                  placeholder="+1 555 000 0000"
                  value={guestPhone}
                  onChange={(e) => setGuestPhone(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={!guestEmail || addGuestMutation.isPending}
                  onClick={() => {
                    addGuestMutation.mutate(
                      { jsaId, email: guestEmail, name: guestName || undefined, phone: guestPhone || undefined },
                      {
                        onSuccess: (data) => {
                          setGuestEmail('');
                          setGuestName('');
                          setGuestPhone('');
                          setShowGuestForm(false);
                          if (data.token) {
                            const url = buildGuestSignUrl(data.token);
                            navigator.clipboard.writeText(url);
                            toast.success('Guest invited — link copied', {
                              description: 'Signing link has been copied to your clipboard.',
                            });
                          }
                        },
                      }
                    );
                  }}
                >
                  Send Invite
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowGuestForm(false);
                    setGuestEmail('');
                    setGuestName('');
                    setGuestPhone('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Signatures Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Crew Signatures ({jsa.signatures.length})</CardTitle>
          {canSign && !showSignForm && !hasCurrentUserSigned && (
            <Button size="sm" onClick={() => { setAcknowledgementData(null); setPlanInitials([]); setShowDrawSignature(false); setShowSignForm(true); }}>
              <PenLine className="h-4 w-4 mr-1" />
              Add Signature
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <OfflineSignatureIndicator onSync={handleOfflineSync} />
          {acknowledgeSuccess && (
            <Alert>
              <AlertDescription>
                Signed as {acknowledgeSuccess.name} ({acknowledgeSuccess.role}) at {acknowledgeSuccess.time}
              </AlertDescription>
            </Alert>
          )}

          {jsa.signatures.length > 0 && (
            <div className="space-y-4">
              {jsa.signatures.map((sig) => (
                <div key={sig.id} className="flex items-start gap-4 p-3 border rounded-md">
                  <div className="flex-1">
                    <p className="font-medium">
                      {sig.signer_name}
                      {sig.signer_user_id && (
                        <Badge variant="outline" className="ml-2 text-xs">Verified</Badge>
                      )}
                    </p>
                    <p className="text-sm text-gray-600">{sig.signer_role}</p>
                    <p className="text-xs text-gray-400">
                      {formatDateTimeInTimeZone(sig.signed_at, timeZone)}
                    </p>
                    {sig.cert_warning && (
                      <p className="text-xs text-amber-600 mt-1">⚠ {sig.cert_warning}</p>
                    )}
                    <SignedPlanRevisions
                      rescuePlan={sig.rescue_plan_revision_detail}
                      safeWorkPlan={sig.safe_work_plan_revision_detail}
                    />
                  </div>
                  {sig.signature_data && (
                    <SignaturePad
                      readOnly
                      signatureData={sig.signature_data}
                      onAccept={() => {}}
                      width={200}
                      height={70}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {jsa.signatures.length === 0 && !showSignForm && (
            <p className="text-sm text-gray-500">No crew signatures collected yet.</p>
          )}

          {signedGuests.length > 0 && (
            <div className="pt-4 border-t space-y-2">
              <p className="text-sm font-medium flex items-center gap-1">
                <Mail className="h-4 w-4" /> Guest signatures ({signedGuests.length})
              </p>
              {signedGuests.map((guest) => (
                <div key={guest.id} className="flex items-start gap-4 p-3 border rounded-md">
                  <div className="flex-1">
                    <p className="font-medium">
                      {guest.name || guest.email}
                      <Badge className="ml-2 text-xs bg-purple-100 text-purple-700 border-purple-200">Guest</Badge>
                    </p>
                    <p className="text-sm text-gray-600">{guest.email}</p>
                    {guest.signed_at && (
                      <p className="text-xs text-gray-400">{formatDateTimeInTimeZone(guest.signed_at, timeZone)}</p>
                    )}
                    <SignedPlanRevisions
                      rescuePlan={guest.rescue_plan_revision_detail}
                      safeWorkPlan={guest.safe_work_plan_revision_detail}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {showSignForm && canSign && (
            <div className="border rounded-md p-4 space-y-4 bg-gray-50">
              <h4 className="font-medium text-sm">Add Your Signature</h4>

              <div className="p-4 border rounded-md bg-white space-y-3">
                <h5 className="text-sm font-medium text-gray-700">Review each hazard</h5>
                <JSAHazardCarousel hazards={jsa.hazards} onComplete={setAcknowledgementData} />
              </div>

              <div className="p-4 border rounded-md bg-white">
                <PlanInitialsFields
                  plans={plansForPerson(user?.person_id)}
                  values={planInitials}
                  onChange={setPlanInitials}
                  disabled={signMutation.isPending || acknowledgeMutation.isPending}
                  timeZone={timeZone}
                />
              </div>

              {acknowledgementData && (
                <>
                  {/* Primary action: I Acknowledge & Sign */}
                  <div className="p-4 border rounded-md bg-white">
                    <Button
                      className="w-full"
                      size="lg"
                      onClick={handleAcknowledgeSign}
                      disabled={acknowledgeMutation.isPending}
                    >
                      <CheckCircle2 className="h-5 w-5 mr-2" />
                      {acknowledgeMutation.isPending ? 'Signing...' : 'I Acknowledge & Sign'}
                    </Button>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Your identity will be captured automatically from your login.
                    </p>
                  </div>

                  {/* Secondary action: draw signature */}
                  {!showDrawSignature && (
                    <button
                      type="button"
                      className="text-sm text-blue-600 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
                      onClick={() => setShowDrawSignature(true)}
                    >
                      Or draw your signature
                    </button>
                  )}
                </>
              )}

              {showDrawSignature && acknowledgementData && (
                <div className="border-t pt-4 space-y-3">
                  <p className="text-sm font-medium text-gray-700">Draw your signature (optional)</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label htmlFor="signer-name">Name *</Label>
                      <Input
                        id="signer-name"
                        value={signerName}
                        onChange={(e) => setSignerName(e.target.value)}
                        placeholder="Full name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="signer-role">Role *</Label>
                      <Input
                        id="signer-role"
                        value={signerRole}
                        onChange={(e) => setSignerRole(e.target.value)}
                        placeholder="e.g. Rope Tech Level 3"
                      />
                    </div>
                    <div>
                      <Label htmlFor="signer-email">Email</Label>
                      <Input
                        id="signer-email"
                        type="email"
                        value={signerEmail}
                        onChange={(e) => setSignerEmail(e.target.value)}
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                  {signerName && signerRole ? (
                    <SignaturePad onAccept={handleSignatureAccept} acceptDisabled={!acknowledgementData} />
                  ) : (
                    <p className="text-sm text-gray-500">Enter your name and role to draw a signature.</p>
                  )}
                </div>
              )}

              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => { setShowSignForm(false); setShowDrawSignature(false); setAcknowledgementData(null); setPlanInitials([]); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {jsa.project !== null && (
        <Card id="job-safety-plans">
          <CardHeader>
            <CardTitle>Job Safety Plans</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">Rescue Plan</TableCell>
                  <TableCell>
                    {jsa.rescue_plan_revision ? (
                      <Link
                        to={`/safety/rescue-plans/${jsa.rescue_plan_revision.id}`}
                        className="font-medium text-indigo-600 hover:underline"
                      >
                        {jsa.rescue_plan_revision.title}
                      </Link>
                    ) : (
                      <span className="text-amber-700">No rescue plan for this job</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {jsa.rescue_plan_revision ? `v${jsa.rescue_plan_revision.version}` : '—'}
                  </TableCell>
                  <TableCell>
                    {jsa.rescue_plan_revision ? (
                      <div className="space-y-1">
                        <Badge
                          variant="outline"
                          className={jsa.rescue_plan_revision.is_superseded
                            ? 'border-gray-300 bg-gray-100 text-gray-600'
                            : 'capitalize'}
                        >
                          {jsa.rescue_plan_revision.is_superseded
                            ? 'Superseded'
                            : jsa.rescue_plan_revision.status.replace('_', ' ')}
                        </Badge>
                        {jsa.rescue_plan_revision.current_revision && (
                          <Link
                            to={`/safety/rescue-plans/${jsa.rescue_plan_revision.current_revision.id}`}
                            className="block text-xs text-indigo-600 hover:underline"
                          >
                            Current: v{jsa.rescue_plan_revision.current_revision.version}
                          </Link>
                        )}
                      </div>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Safe Work Plan</TableCell>
                  <TableCell>
                    {jsa.safe_work_plan_revision ? (
                      <Link
                        to={`/safety/work-plans/${jsa.safe_work_plan_revision.id}`}
                        className="font-medium text-indigo-600 hover:underline"
                      >
                        {jsa.safe_work_plan_revision.title}
                      </Link>
                    ) : (
                      <span className="text-gray-500">No Safe Work Plan for this job</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {jsa.safe_work_plan_revision ? `v${jsa.safe_work_plan_revision.version}` : '—'}
                  </TableCell>
                  <TableCell>
                    {jsa.safe_work_plan_revision ? (
                      <div className="space-y-1">
                        <Badge
                          variant="outline"
                          className={jsa.safe_work_plan_revision.is_superseded
                            ? 'border-gray-300 bg-gray-100 text-gray-600'
                            : 'capitalize'}
                        >
                          {jsa.safe_work_plan_revision.is_superseded
                            ? 'Superseded'
                            : jsa.safe_work_plan_revision.status.replace('_', ' ')}
                        </Badge>
                        {jsa.safe_work_plan_revision.current_revision && (
                          <Link
                            to={`/safety/work-plans/${jsa.safe_work_plan_revision.current_revision.id}`}
                            className="block text-xs text-indigo-600 hover:underline"
                          >
                            Current: v{jsa.safe_work_plan_revision.current_revision.version}
                          </Link>
                        )}
                      </div>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            {!rescuePlanIsSigned && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-t p-4">
                <p className="text-sm text-amber-700">
                  A signed-off rescue plan is required before this JSA can be sent for signatures.
                </p>
                {rescuePlanRemediationHref && (
                  <Button variant="outline" size="sm" asChild>
                    <Link to={rescuePlanRemediationHref}>{rescuePlanRemediationLabel}</Link>
                  </Button>
                )}
              </div>
            )}
            {jsa.safe_work_plan_warning && (
              <div className="border-t bg-amber-50 p-4 text-sm text-amber-800">
                Safe Work Plan warning: this job has no approved or active Safe Work Plan.
                This warning does not block JSA submission.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Projectless JSAs retain the legacy linked-plan workflow. */}
      {jsa.project === null && (jsa.linked_rescue_plans?.length || canLinkRescuePlan) && (
        <Card id="linked-rescue-plans">
          <CardHeader>
            <CardTitle>Linked Rescue Plans</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {jsa.linked_rescue_plans && jsa.linked_rescue_plans.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Signed Off</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jsa.linked_rescue_plans.map((rp) => (
                    <TableRow key={rp.id}>
                      <TableCell>
                        <Link to={`/safety/rescue-plans/${rp.id}`} className="text-indigo-600 hover:underline font-medium">
                          {rp.title}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {rp.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {rp.signed_off_at ? formatDateTimeInTimeZone(rp.signed_off_at, timeZone) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {canLinkRescuePlan && rescuePlansError && (
              <p role="alert" className="p-4 text-sm text-red-700">
                Rescue plans could not be loaded. Refresh the page and try again.
              </p>
            )}
            {canLinkRescuePlan && !rescuePlansError && candidateRescuePlans.length > 0 && (
              <ErrorBoundary
                key={rescuePlansUpdatedAt}
                fallback={
                  <p role="alert" className="p-4 text-sm text-red-700">
                    Rescue plan picker could not be displayed. Refresh the page and try again.
                  </p>
                }
              >
                <RescuePlanPicker
                  plans={candidateRescuePlans}
                  jsaProjectId={jsa.project}
                  jsaProjectName={
                    jsa.project_display?.name ||
                    jsa.project_name ||
                    (jsa.project ? `project #${jsa.project}` : 'this JSA')
                  }
                  selectedPlanId={selectedRescuePlanId}
                  isLinking={linkRescuePlanMutation.isPending}
                  onSelectedPlanChange={setSelectedRescuePlanId}
                  onLink={() => {
                    const planId = parseInt(selectedRescuePlanId, 10);
                    if (!planId) return;
                    linkRescuePlanMutation.mutate(
                      { rescuePlanId: planId, jsaId: jsa.id },
                      {
                        onSuccess: () => {
                          toast.success('Rescue plan linked to JSA.');
                          setSelectedRescuePlanId('');
                        },
                        onError: (error) => {
                          toast.error(extractErrorMessage(error as Error, 'Failed to link rescue plan.'));
                        },
                      }
                    );
                  }}
                />
              </ErrorBoundary>
            )}
            {canLinkRescuePlan &&
              !rescuePlansError &&
              !rescuePlansLoading &&
              candidateRescuePlans.length === 0 &&
              !jsa.linked_rescue_plans?.length && (
              <p className="p-4 text-sm text-gray-500">
                No approved or signed-off rescue plans exist in this organization.{' '}
                <Link
                  to="/safety/rescue-plans?status=pending_review"
                  className="font-medium text-indigo-600 hover:underline"
                >
                  Sign off a rescue plan
                </Link>{' '}
                or{' '}
                <Link
                  to={`/safety/rescue-plans/new?from_jsa=${jsa.id}`}
                  className="font-medium text-indigo-600 hover:underline"
                >
                  Create rescue plan
                </Link>
                .
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Rescue plan override reason (visible when submitted without signed plan) */}
      {jsa.rescue_plan_override_reason && noRescuePlanLinked && (
        <Alert className="border-amber-300 bg-amber-50 text-amber-950 [&>svg]:text-amber-600">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No rescue plan linked</AlertTitle>
          <AlertDescription>
            <span className="font-medium">Reason given at submit:</span>{' '}
            {jsa.rescue_plan_override_reason}
            {jsa.rescue_plan_override_by_username && (
              <span className="text-sm text-gray-500">
                {' '}· by {jsa.rescue_plan_override_by_username}
                {jsa.rescue_plan_override_at && ` at ${formatDateTimeInTimeZone(jsa.rescue_plan_override_at, timeZone)}`}
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}
      {jsa.rescue_plan_override_reason && !noRescuePlanLinked && (
        <Alert>
          <AlertDescription className="flex flex-wrap items-center gap-2">
            <Badge variant="destructive" className="bg-red-100 text-red-800 hover:bg-red-100">Submitted without rescue plan</Badge>
            <span className="font-medium">Rescue Plan Override:</span>{' '}
            {jsa.rescue_plan_override_reason}
            {jsa.rescue_plan_override_by_username && (
              <span className="text-sm text-gray-500">
                · by {jsa.rescue_plan_override_by_username}
                {jsa.rescue_plan_override_at && ` at ${formatDateTimeInTimeZone(jsa.rescue_plan_override_at, timeZone)}`}
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Supervision advisory (S10 — visible when crew module active and no L3 signer) */}
      {jsa.supervision_advisory && (
        <Alert>
          <AlertDescription>
            <span className="font-medium">⚠ Supervision Advisory:</span>{' '}
            {jsa.supervision_advisory}
          </AlertDescription>
        </Alert>
      )}

      {jsa.needs_revalidation && (
        <Alert
          variant="destructive"
          className="border-amber-300 bg-amber-50 text-amber-950 [&>svg]:text-amber-600"
        >
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Re-validation required</AlertTitle>
          <AlertDescription>
            <span className="font-medium">Trigger:</span>{' '}
            {activeRevalidation?.trigger_detail || activeRevalidation?.trigger.replace('_', ' ') || 'This JSA needs supervisor re-validation.'}
            {activeRevalidation?.triggered_by_name ? ` · Flagged by ${activeRevalidation.triggered_by_name}` : ''}
          </AlertDescription>
        </Alert>
      )}

      {jsa.status === 'active' && (jsa.unlisted_crew?.length ?? 0) > 0 && (
        <Alert className="border-yellow-300 bg-yellow-50 text-yellow-950 [&>svg]:text-yellow-600">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {jsa.unlisted_crew!.length} crew dispatched to this job after this JSA was signed
          </AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-3">
            <span>{jsa.unlisted_crew!.map((c) => c.name).join(', ')}</span>
            {(isAdmin || isManager) && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  addDispatchedCrewMutation.mutate(jsa.id, {
                    onError: (error) => {
                      toast.error(extractErrorMessage(error, 'Failed to add crew to JSA.'));
                    },
                  })
                }
                disabled={addDispatchedCrewMutation.isPending}
              >
                {addDispatchedCrewMutation.isPending ? 'Adding...' : 'Add and request signatures'}
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      {jsa.plan_revision_updates?.map((revision) => (
        <Alert key={`${revision.plan_type}-${revision.plan_revision_id}`} className="border-amber-300 bg-amber-50 text-amber-950 [&>svg]:text-amber-600">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Plan updated to revision {revision.version} — {revision.missing_count} crew haven't initialled</AlertTitle>
          <AlertDescription>
            <Link className="underline" to={`/safety/${revision.plan_type === 'safe_work_plan' ? 'work-plans' : 'rescue-plans'}/${revision.plan_revision_id}`}>Open the current {revision.plan_type === 'safe_work_plan' ? 'work' : 'rescue'} plan</Link>. Work can continue while initials are collected.
          </AlertDescription>
        </Alert>
      ))}
      {/* Workflow Actions */}
      {submissionBlockReason && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Submission blocked</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>{submissionBlockReason}</span>
            {rescuePlanRemediationHref && (
              <Button variant="outline" size="sm" asChild>
                <Link to={rescuePlanRemediationHref}>{rescuePlanRemediationLabel}</Link>
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}
      <div className="flex justify-end gap-3">
        {isEditable && canManageSafety && (
          <Button
            variant="outline"
            onClick={() => setShowDiscardConfirm(true)}
            disabled={deleteMutation.isPending}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Discard draft
          </Button>
        )}
        {isEditable && canManageSafety && (
          <Button
            onClick={() => {
              const hasChanges = (
                weatherConditions !== (jsa.weather_conditions || '') ||
                windSpeed !== (jsa.wind_speed || '') ||
                temperature !== (jsa.temperature || '')
              );
              const doSubmit = () => handleSubmitForSignatures();
              if (hasChanges) {
                updateMutation.mutate(
                  { id: jsa.id, data: { weather_conditions: weatherConditions, wind_speed: windSpeed, temperature } },
                  { onSuccess: () => doSubmit(), onError: () => toast.error('Failed to save weather before submitting.') },
                );
              } else {
                doSubmit();
              }
            }}
          >
            <FileText className="h-4 w-4 mr-1" />
            {submitMutation.isPending ? 'Submitting...' : 'Submit for Signatures'}
          </Button>
        )}
        {jsa.status === 'pending_signatures' &&
          !jsa.needs_revalidation &&
          canActivateJSA &&
          canManageSafety && (
          <Button
            variant="outline"
            onClick={() => activateMutation.mutate(jsa.id, {
              onError: (error) => {
                toast.error(extractErrorMessage(error, 'Failed to activate JSA.'));
              },
            })}
            disabled={activateMutation.isPending}
          >
            {activateMutation.isPending ? 'Activating...' : 'Activate JSA'}
          </Button>
        )}
        {jsa.status === 'active' && (
          <Button
            variant="outline"
            onClick={() => closeMutation.mutate(jsa.id, {
              onError: (error) => {
                toast.error(extractErrorMessage(error, 'Failed to close JSA.'));
              },
            })}
            disabled={closeMutation.isPending}
          >
            {closeMutation.isPending ? 'Closing...' : 'Close JSA'}
          </Button>
        )}
        {jsa.needs_revalidation && canResolveJSARevalidation && (
          <>
            <Button
              variant="outline"
              onClick={() => {
                const note = window.prompt(
                  'Keep existing signatures and refresh the expected crew. New crew members must sign. Optional note:',
                  '',
                )?.trim();
                reaffirmRevalidationMutation.mutate(
                  { id: jsa.id, note: note || undefined },
                  {
                    onError: (error) => {
                      if (
                        openSignerDispositionDialog(error, {
                          action: 'reaffirm',
                          note: note || undefined,
                        })
                      ) {
                        return;
                      }
                      toast.error(extractErrorMessage(error, 'Failed to keep signatures and refresh crew.'));
                    },
                  },
                );
              }}
              disabled={reaffirmRevalidationMutation.isPending}
            >
              {reaffirmRevalidationMutation.isPending ? 'Refreshing crew...' : 'Keep signatures; refresh crew'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const note = window.prompt(
                  'Clear all crew, guest, and kiosk signatures and re-open this JSA for signing. Optional note:',
                  '',
                )?.trim();
                requireResignMutation.mutate(
                  { id: jsa.id, note: note || undefined },
                  {
                    onError: (error) => {
                      if (
                        openSignerDispositionDialog(error, {
                          action: 'resign',
                          note: note || undefined,
                        })
                      ) {
                        return;
                      }
                      toast.error(extractErrorMessage(error, 'Failed to clear signatures and re-open JSA.'));
                    },
                  },
                );
              }}
              disabled={requireResignMutation.isPending}
            >
              {requireResignMutation.isPending ? 'Clearing signatures...' : 'Clear signatures; re-open'}
            </Button>
          </>
        )}
      </div>

      {/* Discard Confirmation Dialog */}
      <AlertDialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this JSA?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{jsa.title}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate(jsa.id, {
                onSuccess: () => navigate('/safety/jsas'),
              })}
              className="bg-red-600 hover:bg-red-700"
            >
              Discard JSA
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate Confirmation Dialog */}
      <AlertDialog open={showDuplicateConfirm} onOpenChange={setShowDuplicateConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicate this JSA?</AlertDialogTitle>
            <AlertDialogDescription>
              A new draft JSA will be created from <strong>{jsa.title}</strong> with the same hazards and crew list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowDuplicateConfirm(false);
                duplicateMutation.mutate(jsa.id, {
                  onSuccess: (newJSA) => {
                    navigate(`/safety/jsas/${newJSA.id}`);
                  },
                });
              }}
            >
              Duplicate JSA
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Kiosk Sign Modal */}
      <KioskSignModal
        open={kioskLineId !== null}
        onOpenChange={(open) => { if (!open) setKioskLineId(null); }}
        jsaId={jsaId}
        signatureLineId={kioskLineId ?? 0}
        hazards={jsa.hazards}
        plans={plansForPerson(
          expectedSigners?.find((signer) => signer.id === kioskLineId)?.tech_id,
        )}
        timeZone={timeZone}
        pinMode={pinMode}
        onSuccess={(result: KioskSignResult) => {
          // Only optimistically mark the row as signed when the returned signer
          // identity matches the expected signer for that row. Guest/offline
          // queued results have no signer_user_id, so they are accepted here and
          // reconciled by the server at sync time.
          if (!result.queued) {
            const expectedSigner = expectedSigners?.find((s) => s.id === result.signature_line_id);
            if (
              expectedSigner?.user_id != null &&
              result.signer_user_id != null &&
              expectedSigner.user_id !== result.signer_user_id
            ) {
              setKioskLineId(null);
              toast.warning('This signature does not match the selected crew row. Please sign your own assigned row.');
              return;
            }
          }

          setKioskSigs((prev) => ({
            ...prev,
            [result.signature_line_id]: {
              signer_name: result.signer_name,
              signed_at: result.signed_at,
              queued: result.queued,
            },
          }));
          setKioskLineId(null);
          if (result.queued) {
            toast.success('Signature queued — will sync when you\'re back online');
          } else {
            queryClient.invalidateQueries({ queryKey: queryKeys.jsas.detail(jsaId) });
            queryClient.invalidateQueries({ queryKey: [...queryKeys.jsas.detail(jsaId), 'expected-signers'] });
            queryClient.invalidateQueries({ queryKey: queryKeys.jsas.all });
          }
          if (result.cert_warning) {
            toast.warning(result.cert_warning);
          }
        }}
      />

      {/* Signing QR Code Modal */}
      <QRCodeModal
        open={showSigningQR}
        onOpenChange={setShowSigningQR}
        qrDataUrl={signingQRData?.qr_data_url}
        title={jsa.title}
        subtitle={`${jsa.signing_progress} crew signed`}
        isLoading={signingQRLoading}
      />

      {/* Guest Signing QR Dialog */}
      <Dialog open={guestQRToken !== null} onOpenChange={(open) => { if (!open) setGuestQRToken(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <UIDialogTitle>Guest Signing QR</UIDialogTitle>
          </DialogHeader>
          {guestQRToken && (
            <div className="flex flex-col items-center gap-4 py-4">
              <QRCodeSVG value={buildGuestSignUrl(guestQRToken)} size={224} />
              <p className="text-sm text-gray-500 text-center break-all px-2">
                {buildGuestSignUrl(guestQRToken)}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCopyGuestLink(guestQRToken)}
              >
                <Link2 className="h-4 w-4 mr-1" />
                Copy Link
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Expected Signer Disposition Dialog */}
      <AlertDialog open={showSignerDisposition} onOpenChange={setShowSignerDisposition}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Crew Members Without Accounts</AlertDialogTitle>
            <AlertDialogDescription>
              The following expected signers do not have a linked H3ight user account.
              Choose how to handle each one before{' '}
              {signerDispositionAction.action === 'submit'
                ? 'submitting the JSA for signatures.'
                : 'completing re-validation.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-6 pb-2 space-y-4 max-h-[60vh] overflow-y-auto">
            {noAccountSigners.map((signer) => {
              const current = signerDispositions.find((d) => d.tech_id === signer.tech_id);
              const isAdvisory = current?.disposition === 'advisory';
              const isInvited = current?.disposition === 'invited';
              const noEmail = !signer.email;
              return (
                <div key={signer.tech_id} className="border rounded-md p-3 space-y-2">
                  <div className="font-medium">
                    {signer.name} <span className="text-gray-500 text-sm">({signer.role || 'no role'})</span>
                  </div>
                  {signer.email ? (
                    <div className="text-sm text-gray-600">{signer.email}</div>
                  ) : (
                    <div className="text-sm text-amber-600">No email on file</div>
                  )}
                  <Select
                    value={current?.disposition || ''}
                    onValueChange={(value) => {
                      setSignerDispositions((prev) => {
                        const filtered = prev.filter((d) => d.tech_id !== signer.tech_id);
                        return [
                          ...filtered,
                          {
                            tech_id: signer.tech_id,
                            disposition: value as SignerDispositionEntry['disposition'],
                            advisory_reason: '',
                          },
                        ];
                      });
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Choose disposition..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="invited" disabled={noEmail}>Invite to create an account</SelectItem>
                      <SelectItem value="kiosk">Capture in-person (kiosk/guest)</SelectItem>
                      <SelectItem value="advisory">Advisory-only for this JSA</SelectItem>
                    </SelectContent>
                  </Select>
                  {noEmail && isInvited && (
                    <div className="text-xs text-amber-600">
                      An email is required to send an invitation. Choose kiosk or advisory for signers without an email.
                    </div>
                  )}
                  {isAdvisory && (
                    <textarea
                      className="w-full rounded-md border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      rows={2}
                      placeholder="Reason for marking advisory-only..."
                      value={current?.advisory_reason || ''}
                      onChange={(e) => {
                        setSignerDispositions((prev) =>
                          prev.map((d) =>
                            d.tech_id === signer.tech_id ? { ...d, advisory_reason: e.target.value } : d
                          )
                        );
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowSignerDisposition(false);
                setSignerDispositions([]);
                setNoAccountSigners([]);
                setSignerDispositionAction({ action: 'submit' });
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={
                noAccountSigners.length === 0 ||
                signerDispositions.length !== noAccountSigners.length ||
                signerDispositions.some(
                  (d) => d.disposition === 'advisory' && !d.advisory_reason?.trim()
                ) ||
                signerDispositions.some(
                  (d) => d.disposition === 'invited' && !noAccountSigners.find((s) => s.tech_id === d.tech_id)?.email
                ) ||
                submitMutation.isPending ||
                reaffirmRevalidationMutation.isPending ||
                requireResignMutation.isPending
              }
              onClick={() => {
                if (signerDispositionAction.action === 'reaffirm') {
                  reaffirmRevalidationMutation.mutate({
                    id: jsa.id,
                    note: signerDispositionAction.note,
                    signer_dispositions: signerDispositions,
                  });
                } else if (signerDispositionAction.action === 'resign') {
                  requireResignMutation.mutate({
                    id: jsa.id,
                    note: signerDispositionAction.note,
                    signer_dispositions: signerDispositions,
                  });
                } else {
                  handleSubmitForSignatures({
                    signer_dispositions: signerDispositions,
                  });
                }
                setShowSignerDisposition(false);
                setSignerDispositionAction({ action: 'submit' });
              }}
            >
              {signerDispositionAction.action === 'reaffirm'
                ? 'Keep signatures and continue'
                : signerDispositionAction.action === 'resign'
                  ? 'Clear signatures and continue'
                  : 'Submit'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Re-Disposition Dialog for no-account expected signers */}
      <AlertDialog open={showReDisposition} onOpenChange={setShowReDisposition}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Change signer disposition</AlertDialogTitle>
            <AlertDialogDescription>
              {reDispositionSigner ? (
                <>
                  Choose how to handle <span className="font-medium">{reDispositionSigner.name}</span> on this JSA.
                </>
              ) : (
                'Choose how to handle this signer on this JSA.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-6 pb-2 space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Disposition</Label>
              <Select
                value={reDispositionValue}
                onValueChange={(value) => setReDispositionValue(value as SignerDisposition)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose disposition..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="invited">
                    Invite to create an account
                  </SelectItem>
                  <SelectItem value="kiosk">Capture in-person (kiosk/guest)</SelectItem>
                  <SelectItem value="advisory">Advisory-only for this JSA</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {reDispositionValue === 'advisory' && (
              <div className="space-y-2">
                <Label htmlFor="re-disposition-reason" className="text-sm font-medium">
                  Advisory reason
                </Label>
                <textarea
                  id="re-disposition-reason"
                  className="w-full rounded-md border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={3}
                  placeholder="Reason for marking advisory-only..."
                  value={reDispositionReason}
                  onChange={(e) => setReDispositionReason(e.target.value)}
                />
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setReDispositionSigner(null);
                setReDispositionValue('');
                setReDispositionReason('');
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={
                !reDispositionValue ||
                (reDispositionValue === 'advisory' && !reDispositionReason.trim()) ||
                reDispositionMutation.isPending
              }
              onClick={() => {
                if (!jsa || !reDispositionSigner || !reDispositionValue) return;
                const payload: ReDispositionPayload = {
                  id: jsa.id,
                  expected_signer_id: reDispositionSigner.id!,
                  disposition: reDispositionValue,
                  advisory_reason: reDispositionReason,
                };
                reDispositionMutation.mutate(payload, {
                  onSuccess: () => {
                    setShowReDisposition(false);
                    setReDispositionSigner(null);
                    setReDispositionValue('');
                    setReDispositionReason('');
                    toast.success('Signer disposition updated.');
                  },
                  onError: (error) => {
                    toast.error(extractErrorMessage(error, 'Failed to update disposition.'));
                  },
                });
              }}
            >
              Update
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rescue Plan Override Dialog */}
      <AlertDialog open={showRescuePlanOverride} onOpenChange={setShowRescuePlanOverride}>
        <AlertDialogContent
          id="rescue-plan-override-dialog"
          data-required-validation-boundary="true"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Rescue Plan Not Signed Off</AlertDialogTitle>
            <AlertDialogDescription>
              {rescuePlanWarningMsg}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-6">
            {rescuePlanRemediationHref ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowRescuePlanOverride(false);
                  navigate(rescuePlanRemediationHref);
                }}
              >
                {rescuePlanRemediationLabel}
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">
                Ask an admin or manager to link a signed rescue plan.
              </p>
            )}
          </div>
          <div className="px-6 pb-2">
            <Label htmlFor="override-reason" className="text-sm font-medium">
              Reason for proceeding without a signed rescue plan *
            </Label>
            <textarea
              id="override-reason"
              className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              rows={3}
              value={overrideReason}
              onChange={(e) => {
                setOverrideReason(e.target.value);
                if (e.target.value.trim()) setOverrideReasonError(null);
              }}
              placeholder="Explain why it is safe to proceed..."
              required
            />
            {overrideReasonError && (
              <p className="mt-1 text-sm text-red-600">{overrideReasonError}</p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setOverrideReason('');
              setOverrideReasonError(null);
              setRescuePlanWarningMsg('');
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={submitMutation.isPending}
              data-validation-scope="rescue-plan-override-dialog"
              onClick={(event) => {
                event.preventDefault();
                const reason = overrideReason.trim();
                if (!reason) {
                  setOverrideReasonError('Override reason is required.');
                  return;
                }

                const payload: SubmitJSAPayload = {
                  id: jsa.id,
                  override_rescue_plan: true,
                  override_reason: reason,
                };
                if (signerDispositions.length > 0) {
                  payload.signer_dispositions = signerDispositions;
                }
                submitMutation.mutate(payload, {
                  onError: (error) => {
                    if (
                      isAxiosError<{
                        no_account_signers_required?: boolean;
                        no_account_signers?: NoAccountSigner[];
                      }>(error) &&
                      error.response?.status === 409 &&
                      error.response.data?.no_account_signers_required
                    ) {
                      setNoAccountSigners(error.response.data.no_account_signers || []);
                      setSignerDispositionAction({ action: 'submit' });
                      setShowRescuePlanOverride(false);
                      setShowSignerDisposition(true);
                      return;
                    }
                    toast.error(extractErrorMessage(error, 'Failed to submit JSA for signatures.'));
                  },
                  onSuccess: () => {
                    setShowRescuePlanOverride(false);
                    setOverrideReason('');
                    setOverrideReasonError(null);
                    setRescuePlanWarningMsg('');
                  },
                });
              }}
            >
              Submit Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
